import {
  ESdChangeReason,
  type IComponentDefinition,
  type IDisplayItem,
  type IDisplayItemComponent,
  type IProject,
  type IScreenDesign,
  type IScreenDesignDictionary
} from "@kuika/kuika-cl-model";
import type { IScreenDesignContextState } from "../../contexts/screen-design-context/screen-design-context";
import { applyChildItemSizeDefaults } from "./component-children-host";
import { ScreenDesignHelper } from "./screen-design-helper";

/**
 * Adding and removing a component on a screen the caller has already resolved — KOA-18431.
 *
 * ## Why this is its own module
 *
 * Two callers need the same two tree operations and neither can use the platform's own:
 * `DragDrop.addChildItemByComponentName` / `deleteItemByKuikaId` reach the tree through the
 * PROVIDER, so they always act on the screen the designer has OPEN.
 *
 *   - `screen-composition.ts` builds them into the placement rules the DSL and the palette share;
 *   - a DETACHED screen session exposes them as the provider methods of its own screen, because
 *     the platform's own writers call them. `applyTableFieldBinding` — the one path that binds a
 *     table cell correctly — deletes and re-creates the column's edit-mode renderer through
 *     exactly these two. A session that refuses them cannot bind a cell at all.
 *
 * So the operations take the RESOLVED context and its screen, and neither knows that detached
 * sessions exist. `screen-composition` resolves through `resolveEditTarget`; the session passes its
 * own overlay. That is also why this module imports neither — a module that resolved the target
 * itself would close an import cycle with the session that hosts it.
 *
 * ## What is NOT here
 *
 * The placement RULES — what the palette offers, what a host declares as a child item, what region
 * the platform owns. Those live in `screen-composition.ts` and are the caller's to apply. These two
 * functions are the mechanism underneath them: build the subtree the platform's own way, put it
 * where it was asked, publish the screen.
 */

/** The provider partition that supplies a component's default styling. */
interface ISdWithDefaultStyles {
  defaultStyleProvider?: unknown;
}

export interface ITreeOpRefusal {
  error: string;
}

export interface IInsertComponentResult {
  /** The id the platform gave the new component. */
  kuikaId: number;
  /** The component it went into. */
  hostKuikaId: number;
}

export interface IRemoveComponentResult {
  kuikaId: number;
  /** Every id the removal took off the screen, the component itself included. */
  removedKuikaIds: number[];
}

export function isTreeOpRefusal(
  result: IInsertComponentResult | IRemoveComponentResult | ITreeOpRefusal
): result is ITreeOpRefusal {
  return (result as ITreeOpRefusal).error !== undefined;
}

export interface IInsertComponentInput {
  /** The component that will hold the new one. */
  hostKuikaId: number;
  /** Catalog name, as `createNewComponentByName` takes it. */
  componentName: string;
  /** True for a band that belongs at the top (a table header). Default is last. */
  placeFirst?: boolean;
}

/**
 * Build a catalog component the platform's own way and put it inside `hostKuikaId`.
 *
 * The chain is the platform's, which is why a placed Table arrives with its header, row and first
 * column already inside it:
 *
 *   `createNewComponentByName` → `createNewDisplayItemByComponent` → `addDefaultComponentStylings`
 *   → `addChildForItemContainer`
 *
 * Nothing is persisted; the caller's screen is published through its own `setScreenDesign`.
 */
export function insertComponentIntoHost(
  target: IScreenDesignContextState,
  sdInstance: unknown,
  input: IInsertComponentInput
): IInsertComponentResult | ITreeOpRefusal {
  const project: IProject | undefined = target.project;
  if (project?.componentSetObj?.components === undefined) {
    return { error: "The component catalog is not loaded yet." };
  }

  const dictionary: IScreenDesignDictionary | undefined = target.screenDesignDictionary;
  const host: IDisplayItem | undefined = dictionary?.[input.hostKuikaId];
  if (host === undefined) {
    return { error: `There is no component ${input.hostKuikaId} on this screen to add to.` };
  }

  const component: IDisplayItemComponent | undefined = ScreenDesignHelper.createNewComponentByName(
    input.componentName,
    project,
    false
  );
  if (component === undefined) {
    return { error: `"${input.componentName}" could not be created from the catalog.` };
  }
  const definition: IComponentDefinition | undefined = target.getComponentDefinition?.(component);
  if (definition === undefined) {
    return { error: `"${input.componentName}" has no definition in this project's component set.` };
  }

  const screen: IScreenDesign = target.getScreenDesign();
  const defaultStyleProvider = (sdInstance as ISdWithDefaultStyles)?.defaultStyleProvider;
  let newDi: IDisplayItem = ScreenDesignHelper.createNewDisplayItemByComponent(
    screen,
    dictionary ?? {},
    host.id,
    component,
    project.componentSetObj.components
  );
  newDi = ScreenDesignHelper.addDefaultComponentStylings(newDi, definition, defaultStyleProvider as never);
  ScreenDesignHelper.addChildForItemContainer(
    component,
    newDi,
    screen,
    dictionary ?? {},
    project.componentSetObj.components,
    project.projectType,
    defaultStyleProvider as never
  );
  applyChildItemSizeDefaults(newDi, input.componentName, definition.name);

  host.children = host.children ?? [];
  if (input.placeFirst === true) host.children.unshift(newDi);
  else host.children.push(newDi);
  target.setScreenDesign(screen, ESdChangeReason.DragDropDi, String(newDi.id));

  return { kuikaId: newDi.id, hostKuikaId: host.id };
}

/** Collect a subtree's ids, so a removal can report exactly what left the screen. */
function collectSubtreeIds(di: IDisplayItem | undefined): number[] {
  if (di === undefined) return [];
  const ids: number[] = [];
  const walk = (node: IDisplayItem): void => {
    if (node?.id !== undefined && node.id !== null) ids.push(node.id);
    (node?.children ?? []).forEach(walk);
  };
  walk(di);
  return ids;
}

/**
 * Take a component off the screen.
 *
 * Structural only: the caller decides whether it is ALLOWED to remove this one (the platform-owned
 * regions are `screen-composition`'s rule, not this module's).
 */
export function removeComponentFromTree(
  target: IScreenDesignContextState,
  kuikaId: number
): IRemoveComponentResult | ITreeOpRefusal {
  const dictionary: IScreenDesignDictionary | undefined = target.screenDesignDictionary;
  const di: IDisplayItem | undefined = dictionary?.[kuikaId];
  if (di === undefined) {
    return { error: `There is no component ${kuikaId} on this screen.` };
  }

  const parentId: number | undefined = typeof di.parentId === "number" ? di.parentId : undefined;
  const parent: IDisplayItem | undefined = parentId === undefined ? undefined : dictionary?.[parentId];
  if (parent?.children === undefined) {
    return { error: "This component's parent could not be found, so it was not removed." };
  }

  const removedKuikaIds: number[] = collectSubtreeIds(di);
  parent.children = parent.children.filter((child: IDisplayItem): boolean => child.id !== di.id);
  const screen: IScreenDesign = target.getScreenDesign();
  target.setScreenDesign(screen, ESdChangeReason.DragDropDi, String(parent.id));

  return { kuikaId, removedKuikaIds };
}

/**
 * The two columns the platform creates WITH a table, and nothing else.
 *
 * `addChildForItemContainer` gives a fresh `TableRow` one `TableDefaultColumn` and one
 * `TableTemplateColumn` so the table is not an empty box on the canvas. They are a starting point
 * for a person, and noise for an author writing the columns out: the AI's own render path already
 * clears them before adding the first explicit column (`component-tools.ts`), and a task screen
 * composed through the DSL must read the same — a run that wrote three columns shipped five, two
 * of them headed "Title" and bound to nothing.
 *
 * Recognised by the EXACT starting shape (one of each, nothing else), so a row a person has since
 * worked on is never touched.
 */
export function isAutoCreatedColumnSet(host: IDisplayItem | undefined): boolean {
  const hostName: string = host?.item?.name ?? "";
  if (hostName !== "TableRow" && hostName !== "ReportTableRow") return false;
  const children: IDisplayItem[] = host?.children ?? [];
  if (children.length !== 2) return false;
  const names: string[] = children.map((child: IDisplayItem): string => child.item?.name ?? "");
  if (hostName === "ReportTableRow") {
    return names.every((name: string): boolean => name === "ReportTableColumn");
  }
  return names.includes("TableDefaultColumn") && names.includes("TableTemplateColumn");
}
